import { useState } from "react";
import { Bot, User, Copy, Check } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeSanitize from "rehype-sanitize";
import { theme } from "../../../theme";

type ChatMessageProps = {
  id: string;
  role: "user" | "assistant";
  content: string;
  metadata?: {
    sources?: string[] | null;
    rawContent?: string | null;
    skillApplied?: boolean;
    skillName?: string | null;
    followUpQuestions?: string[];
    provider?: string | null;
    error?: string;
  };
  showSources: boolean;
  isLastAssistant: boolean;
  isRunning: boolean;
  onFollowUp: (question: string) => void;
};

export const ChatMessage = ({
  role,
  content,
  metadata,
  showSources,
  isLastAssistant,
  isRunning,
  onFollowUp,
}: ChatMessageProps) => {
  const [showRaw, setShowRaw] = useState(false);
  const [copied, setCopied] = useState(false);

  const displayContent = showRaw && metadata?.rawContent ? metadata.rawContent : content;
  const skillApplied = metadata?.skillApplied ?? false;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  return (
    <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
      <div
        className={`flex items-start space-x-3 max-w-[80%] ${
          role === "user" ? "flex-row-reverse space-x-reverse" : ""
        }`}
      >
        {/* Avatar */}
        <div className={role === "user" ? theme.chat.userAvatar : theme.chat.botAvatar}>
          {role === "user" ? (
            <User className="w-4 h-4" />
          ) : (
            <Bot className={`w-4 h-4 ${theme.text.accent}`} />
          )}
        </div>

        <div>
          {/* Message bubble */}
          <div className={role === "user" ? theme.chat.userBubble : theme.chat.aiBubble}>
            {role === "user" ? (
              <p className="text-base leading-relaxed whitespace-pre-wrap">{content}</p>
            ) : (
              <>
                <div className="chat-markdown">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    rehypePlugins={[rehypeSanitize]}
                  >
                    {displayContent}
                  </ReactMarkdown>
                </div>

                {/* Raw / Skill-Enhanced toggle */}
                <div className={`flex items-center gap-2 mt-2 pt-2 border-t ${theme.border.subtle}`}>
                  <span className={`text-xs ${theme.text.veryMuted}`}>View:</span>
                  {skillApplied ? (
                    <>
                      <button
                        onClick={() => setShowRaw(false)}
                        className={`text-xs px-2 py-0.5 rounded transition-colors ${
                          !showRaw
                            ? "dark:bg-teal-500/20 bg-teal-50 dark:text-teal-300 text-teal-700 font-medium"
                            : `${theme.text.muted} hover:dark:text-white/70`
                        }`}
                      >
                        Skill-Enhanced
                      </button>
                      <button
                        onClick={() => setShowRaw(true)}
                        className={`text-xs px-2 py-0.5 rounded transition-colors ${
                          showRaw
                            ? "dark:bg-amber-500/20 bg-amber-50 dark:text-amber-300 text-amber-700 font-medium"
                            : `${theme.text.muted} hover:dark:text-white/70`
                        }`}
                      >
                        Raw
                      </button>
                    </>
                  ) : (
                    <span className={`text-xs ${theme.text.veryMuted} opacity-50`}>
                      Raw | Skill-Enhanced (no skill active)
                    </span>
                  )}

                  {/* Copy button */}
                  <button
                    onClick={handleCopy}
                    className={`ml-auto text-xs p-1 rounded transition-colors ${theme.text.muted} hover:dark:text-white/70`}
                    aria-label="Copy message"
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  </button>
                </div>

                {/* Sources */}
                {showSources && metadata?.sources && metadata.sources.length > 0 && (
                  <div className={`mt-2 pt-2 border-t ${theme.border.subtle}`}>
                    <p className={`text-xs ${theme.text.muted}`}>
                      Sources: {metadata.sources.join(", ")}
                    </p>
                  </div>
                )}

                {/* Provider label */}
                {metadata?.provider && (
                  <p className={`text-xs mt-1 ${theme.text.veryMuted}`}>
                    via Knowledge Base
                  </p>
                )}
              </>
            )}
          </div>

          {/* Follow-up suggestions (only on last assistant message) */}
          {role === "assistant" &&
            isLastAssistant &&
            metadata?.followUpQuestions &&
            metadata.followUpQuestions.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {metadata.followUpQuestions.map((question) => (
                  <button
                    key={question}
                    onClick={() => onFollowUp(question)}
                    disabled={isRunning}
                    aria-label={`Ask: ${question}`}
                    className={`${theme.button.pillBase} ${theme.button.pillTeal} disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {question}
                  </button>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
